/**
 * Hand-rolled CSV export for the admin tables.
 *
 * The panel already generates PDFs (jspdf on the Promoter screen, server-side
 * blobs on Billing and Rides), but PDF is the wrong format for data people
 * want to open in Excel. There is no CSV library in the project and this is
 * small enough not to justify adding one.
 *
 * Output follows RFC 4180: CRLF row separators, every field quoted, embedded
 * quotes doubled. A UTF-8 BOM is prepended because Excel otherwise reads the
 * file as the system codepage and mangles non-ASCII names.
 */

import { ReportColumn, ReportValue, reportFileName, reportTotals, toReportText } from '@/misc/report';

const UTF8_BOM = '\uFEFF';
const ROW_SEPARATOR = '\r\n';

/**
 * Every field is quoted rather than only the ones that strictly need it. The
 * rule is then unconditional, which removes a whole class of "we forgot this
 * one contains a comma" bugs — and Excel still type-infers quoted numbers.
 */
function escapeField(value: ReportValue): string {
    return `"${toReportText(value).replace(/"/g, '""')}"`;
}

export function toCsv<T>(rows: T[], columns: ReportColumn<T>[]): string {
    const lines: string[] = [columns.map((column) => escapeField(column.header)).join(',')];

    rows.forEach((row) => {
        lines.push(columns.map((column) => escapeField(column.value(row))).join(','));
    });

    if (columns.some((column) => column.total)) {
        const totals = reportTotals(rows, columns);
        let labelled = false;
        const totalRow = columns.map((column, index) => {
            if (totals[index] !== undefined) {
                return escapeField(totals[index]);
            }
            if (!labelled) {
                labelled = true;
                return escapeField('Total');
            }
            return escapeField(undefined);
        });
        lines.push(totalRow.join(','));
    }

    return UTF8_BOM + lines.join(ROW_SEPARATOR) + ROW_SEPARATOR;
}

/**
 * Triggers a browser download of the given CSV text.
 *
 * Unlike the existing PDF download helpers this cleans up after itself — the
 * anchor is removed and the object URL revoked, so repeated exports do not
 * leak a blob per click.
 */
export function downloadCsv(fileName: string, csv: string): void {
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export function csvFileName(prefix: string, scope: string | null, from: string | null, to: string | null): string {
    return reportFileName(prefix, scope, from, to, 'csv');
}
